/**
 * CarrierLogo — shows a logo for a shipping carrier next to its name.
 *
 *  • Maps known SCACs / carrier names → official domains
 *  • Pulls logo via Clearbit (free, no key)
 *  • Falls back to a deterministic colored initials badge if no logo loads
 *
 * Usage:
 *   <CarrierLogo carrier="MAERSK" size={36} />
 *   <CarrierLogo carrier="MSCU" />            // SCAC also works
 */
import React, { useState, useMemo } from 'react';

// ─── Carrier domain registry ────────────────────────────────────────────────
// Keyed by both 4-letter SCAC and a normalised display name (uppercase, alphanum-only).
const CARRIER_DOMAINS = {
    // Top 20 ocean carriers
    MAEU: 'maersk.com',           MAERSK: 'maersk.com',          MAERSKLINE: 'maersk.com',
    MSCU: 'msc.com',              MSC: 'msc.com',                MEDU: 'msc.com',
    CMDU: 'cma-cgm.com',          CMACGM: 'cma-cgm.com',         CMA: 'cma-cgm.com',
    HLCU: 'hapag-lloyd.com',      HAPAGLLOYD: 'hapag-lloyd.com', HLAG: 'hapag-lloyd.com',
    COSU: 'coscoshipping.com',    COSCO: 'coscoshipping.com',    COSCOSHIPPING: 'coscoshipping.com',
    ONEY: 'one-line.com',         ONE: 'one-line.com',           OCEANNETWORKEXPRESS: 'one-line.com',
    EGLV: 'evergreen-line.com',   EVERGREEN: 'evergreen-line.com', EVERGREENLINE: 'evergreen-line.com',
    YMLU: 'yangming.com',         YANGMING: 'yangming.com',      YML: 'yangming.com',
    OOLU: 'oocl.com',             OOCL: 'oocl.com',
    HDMU: 'hmm21.com',            HMM: 'hmm21.com',
    ZIMU: 'zim.com',              ZIM: 'zim.com',
    APLU: 'apl.com',              APL: 'apl.com',
    PILU: 'pilship.com',          PIL: 'pilship.com',            PACIFICINTERNATIONALLINES: 'pilship.com',
    WHLC: 'wanhai.com',           WANHAI: 'wanhai.com',
    SINO: 'sinokor.co.kr',        SINOKOR: 'sinokor.co.kr',
    KMTU: 'ksline.com',           KMTC: 'ksline.com',            KMTCLINE: 'ksline.com',
    ESSA: 'esline.com',           ESL: 'esline.com',
    MATS: 'matson.com',           MATSON: 'matson.com',
    SAFM: 'safmarine.com',        SAFMARINE: 'safmarine.com',
    SUDU: 'hamburgsud-line.com',  HAMBURGSUD: 'hamburgsud-line.com',
    ANNU: 'cnc-line.com',         CNC: 'cnc-line.com',           CNCLINE: 'cnc-line.com',
    GOLU: 'gold-star.com.tw',     GOLDSTAR: 'gold-star.com.tw',
    SEJJ: 'sealandmaersk.com',    SEALAND: 'sealandmaersk.com',
};

// Stable badge color palette (used for fallback initials chip)
const PALETTE = [
    'bg-indigo-500', 'bg-rose-500', 'bg-emerald-500', 'bg-amber-500',
    'bg-cyan-500', 'bg-violet-500', 'bg-sky-500', 'bg-pink-500',
    'bg-teal-500', 'bg-orange-500',
];

const normalise = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

const initialsOf = (s) => {
    const parts = String(s || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
};

const hashIdx = (s, n) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return h % n;
};

const CarrierLogo = ({ carrier, domain, size = 36, className = '' }) => {
    const [failed, setFailed] = useState(false);

    const { resolvedDomain, initials, paletteCls } = useMemo(() => {
        const norm = normalise(carrier);
        return {
            resolvedDomain: domain || CARRIER_DOMAINS[norm] || null,
            initials: initialsOf(carrier),
            paletteCls: PALETTE[hashIdx(norm || 'X', PALETTE.length)],
        };
    }, [carrier, domain]);

    const px = `${size}px`;
    const fontSize = `${Math.max(10, Math.round(size * 0.42))}px`;

    // No mapping → show initials badge directly (skip the broken-image attempt)
    if (!resolvedDomain || failed) {
        return (
            <div
                aria-label={carrier}
                data-testid={`carrier-logo-fallback-${normalise(carrier)}`}
                className={`inline-flex items-center justify-center rounded-lg text-white font-bold flex-shrink-0 shadow-sm ${paletteCls} ${className}`}
                style={{ width: px, height: px, fontSize }}
            >
                {initials}
            </div>
        );
    }

    return (
        <div
            className={`inline-flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden flex-shrink-0 ${className}`}
            style={{ width: px, height: px }}
            data-testid={`carrier-logo-${normalise(carrier)}`}
        >
            <img
                src={`https://www.google.com/s2/favicons?domain=${resolvedDomain}&sz=256`}
                alt={carrier}
                onError={() => setFailed(true)}
                style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '2px' }}
                loading="lazy"
                referrerPolicy="no-referrer"
            />
        </div>
    );
};

export default CarrierLogo;
